import { AppLink } from '@/components/ui/app-link'
import { spacePathOf } from '@/components/features/parcels/parcelUtils'
import { useAuth } from '@/lib/AuthContext'
import { useMenu } from '@/lib/MenuContext'
import { WORKFLOW_MANAGERS } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import type { MemberBadge } from '@/types/badges'
import { useOriginText } from './badgeText'

const linkClass = 'break-words text-left text-primary underline-offset-2 hover:underline [overflow-wrap:anywhere]'

/**
 * Where a grant came from, as a link when there is a page for it: a course or
 * an event opens its page, a workflow opens the editor only for who manages
 * workflows. A manual grant is plain text.
 *
 * Course and event pages live under their space (`/{section}/{space}/{id}`).
 * The API sends `origin.spaceId` only when the viewer opens that space, so
 * without it (or with a space missing from the menu) the origin is plain text.
 */
export const BadgeOrigin = ({ grant, className }: { grant: Pick<MemberBadge, 'source' | 'origin'> & { sourceRef?: string | null }; className?: string }) => {
  const originText = useOriginText()
  const { hasRole } = useAuth()
  const { sections } = useMenu()
  const text = originText(grant)
  const origin = grant.origin
  const plain = <span className={cn('break-words', className)}>{text}</span>

  if (!origin) return plain

  if (origin.type === 'workflow') {
    if (!hasRole(WORKFLOW_MANAGERS)) return plain
    return <AppLink to={`/workflows?workflow=${encodeURIComponent(origin.id)}`} className={cn(linkClass, className)} data-badge-origin-link="workflow">{text}</AppLink>
  }

  const spacePath = origin.spaceId ? spacePathOf(sections, origin.spaceId) : null
  if (!spacePath) return plain
  return <AppLink to={`${spacePath}/${origin.id}`} className={cn(linkClass, className)} data-badge-origin-link={origin.type}>{text}</AppLink>
}
